import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useIsMobile from "../hooks/useIsMobile";
import { isSignedIn, setSignedIn } from "../utils/authStorage";
import { getUserApplications } from "../API/applicationAPI";
import { getUserProfile } from "../API/profileAPI";
import {
  confirmPayment,
  getPaymentBySessionId,
} from "../API/paymentAPI";
import check from "../assets/check.png";
import cross from "../assets/cross.png";

import {
  buttonStyleSm,
  buttonloginstyle2Sm,
  buttonStyleSecondarySm,
  buttonStyleFiledSm,
} from "../help/Utils";

import { formatDateUSReadable } from "../utils/dateFormatting";
import VisaReportCard from "./visaReportCard";
import { downloadEligibilityReportPdf } from "../API/applicationAPI";
import rightArrow from "../assets/right-arrow.png";
import StartNewApplicationDropdown from "./StartNewApplicationDropdown";
import VisaApplicationCard from "./visaApplicationCard";
import { getDashboardApplicationProgress } from "../utils/dashboardApplicationProgress";
import {
  getApplicationCardCTA,
  getEligibilityCardCTA,
  getEligibilityTier,
  ELIGIBILITY_TIER_CHIP,
  hasStartedApplications,
  isApplicationStarted,
  type DashboardCardButton,
} from "../utils/dashboardCardMappers";
import type { Application, ApplicationStage } from "../types/application";

/** Past this point, "My Visa Applications" cards omit the Eligible / Not Eligible pill (stage matters more). */
const STAGES_HIDE_APP_ELIGIBILITY_CHIP: ApplicationStage[] = [
  "forms_in_progress",
  "forms_completed",
  "under_review",
  "revision_requested",
  "ready_for_uscis",
  "uscis_form_generated",
  "signature_pending",
  "signature_completed",
  "submitted_to_uscis",
  "approved",
  "rejected",
  "closed",
];

interface Button {
  label: DashboardCardButton["label"];
  type: DashboardCardButton["type"];
  link: DashboardCardButton["link"];
}

interface VisaApplicationCardData {
  title: string;
  label: string;
  status: string;
  statusColor: string;
  statusBorderColor: string;
  statusTextColor: string;
  updatedDate: string;
  totalSteps: number;
  completedSteps: number;
  missingDocs: number;
  dotColor: string;
  buttons: Button[];
  applicationId?: string;
  visaType?: string;
  /** Beneficiary the application is for (from API) */
  beneficiaryName?: string;
  hasUnreadFeedback?: boolean;
  feedbackPendingItemCount?: number;
  hasDocumentFeedback?: boolean;
  documentFeedbackPendingCount?: number;
  documentFeedbackItems?: Array<{
    id: number;
    fileName: string;
    documentCategory: string;
    status: string;
  }>;
}

interface VisaReportCardData {
  title: string;
  label: string;
  updatedDate: string;
  beneficiaryName?: string;
  labelBgColor: string;
  labelColor: string;
  buttons: Button[];
  applicationId?: number;
}

const Dashboard: React.FC = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tokenReady, setTokenReady] = useState<boolean>(false);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loadingApplications, setLoadingApplications] = useState<boolean>(true);
  const [applicationsFetchSucceeded, setApplicationsFetchSucceeded] = useState<boolean>(false);
  const [paymentPopup, setPaymentPopup] = useState<{
    type: "success" | "cancel";
    paymentDetails?: any;
  } | null>(null);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [applicationCardsData, setApplicationCardsData] = useState<VisaApplicationCardData[]>([]);

  const handlePaymentSuccess = async (sessionId: string) => {
    try {
      setLoadingPayment(true);
      // Try to get payment details
      const maxRetries = 5;
      const retryDelay = 2000;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const payment = await getPaymentBySessionId(sessionId);
          if (
            payment.status === "completed" ||
            payment.status === "succeeded"
          ) {
            setPaymentPopup({ type: "success", paymentDetails: payment });
            // Clean up URL
            setSearchParams({});
            setLoadingPayment(false);
            return;
          }
          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          }
        } catch (getError: any) {
          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          }
        }
      }

      // If all retries failed, try manual confirmation
      const payment = await confirmPayment(sessionId);
      setPaymentPopup({ type: "success", paymentDetails: payment });
      // Clean up URL
      setSearchParams({});
    } catch (error: any) {
      console.error("Error confirming payment:", error);
      // Still show success popup even if confirmation fails
      setPaymentPopup({ type: "success" });
      setSearchParams({});
    } finally {
      setLoadingPayment(false);
    }
  };

  // Handle payment success/cancel popup
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const paymentStatus = searchParams.get("payment_status");

    if (sessionId && paymentStatus === "success") {
      handlePaymentSuccess(sessionId);
    } else if (paymentStatus === "cancel") {
      setPaymentPopup({ type: "cancel" });
      // Clean up URL
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    const alreadySignedIn = isSignedIn();

    if (urlToken) {
      // Task 149: the OAuth callback already set the HttpOnly session
      // cookie, so the token in the URL is not stored — only the marker is.
      // The profile fetch below is what makes userDetails match the email
      // login flow.
      setSignedIn();
      const finishOAuth = async () => {
        try {
          const res = await getUserProfile();
          const user = res?.data?.data;
          if (user) {
            localStorage.setItem("userDetails", JSON.stringify(user));
            localStorage.setItem("email", user.email ?? "");
          }
        } catch {
          // Continue even if profile fetch fails; the session cookie is set
        }
        window.location.href = "/dashboard";
      };
      finishOAuth();
      setTokenReady(true);
    } else if (alreadySignedIn) {
      setTokenReady(true);
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // Fetch user applications
  useEffect(() => {
    const fetchApplications = async () => {
      if (!tokenReady) return;

      try {
        setLoadingApplications(true);
        console.log("🔄 Fetching applications...");
        const data = await getUserApplications();
        console.log("✅ Fetched applications:", data);
        console.log("📊 Applications count:", data?.length || 0);

        // Ensure data is an array
        if (Array.isArray(data)) {
          setApplicationsFetchSucceeded(true);
          setApplications(data);
          setApplicationCardsData(data.filter((app) => isApplicationStarted(app)).map((app) => {
            const appCta = getApplicationCardCTA(app);
            const { totalSteps, completedSteps } = getDashboardApplicationProgress(app);
            const docTotal = app.documentProgress?.total ?? 0;
            const docCompleted = app.documentProgress?.completed ?? 0;
            const missingDocs =
              app.stage === "payment_completed" || app.stage === "forms_in_progress"
                ? Math.max(0, docTotal - docCompleted)
                : 0;

            const rawBeneficiary =
              typeof app.beneficiaryName === "string" ? app.beneficiaryName.trim() : "";
            const stage = (app.stage || "eligibility_check") as ApplicationStage;
            const hideEligibilityChip = STAGES_HIDE_APP_ELIGIBILITY_CHIP.includes(stage);
            const appTierChip =
              ELIGIBILITY_TIER_CHIP[getEligibilityTier(app) ?? "green"];
            return {
              title: app.visaType,
              label: hideEligibilityChip ? "" : appTierChip.label,
              status: appCta.statusLabel,
              statusColor: appTierChip.bg,
              statusBorderColor: appTierChip.border,
              statusTextColor: appTierChip.text,
              updatedDate: formatDateUSReadable(new Date(app.updatedAt || app.createdAt)),
              totalSteps,
              completedSteps,
              missingDocs,
              dotColor: appTierChip.dot,
              buttons: appCta.extraButton ? [appCta.extraButton, appCta.button] : [appCta.button],
              applicationId: String(app.id),
              visaType: app.visaType,
              beneficiaryName: rawBeneficiary || undefined,
              hasUnreadFeedback: !!app.hasUnreadFeedback,
              feedbackPendingItemCount:
                typeof app.feedbackPendingItemCount === "number"
                  ? app.feedbackPendingItemCount
                  : 0,
              hasDocumentFeedback: !!app.hasDocumentFeedback,
              documentFeedbackPendingCount:
                typeof app.documentFeedbackPendingCount === "number"
                  ? app.documentFeedbackPendingCount
                  : 0,
              documentFeedbackItems: Array.isArray(app.documentFeedbackItems)
                ? app.documentFeedbackItems
                : [],
            };
          }));
        } else {
          console.warn("⚠️ API returned non-array data:", data);
          setApplicationsFetchSucceeded(true);
          setApplicationCardsData([]);
          setApplications([]);
        }
      } catch (error: any) {
        console.error("❌ Error fetching applications:", error);
        console.error("❌ Error details:", {
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });
        // Don't set applications to [] on error - avoid triggering redirect.
        // Leave previous state; user stays on dashboard and can retry.
        setApplicationsFetchSucceeded(false);
      } finally {
        setLoadingApplications(false);
      }
    };

    fetchApplications();
  }, [tokenReady]);

  // When user has no applications (fetch succeeded with empty): redirect to answer-review if pending eligibility, else to Choose Service page
  // Never redirect on API failure - user stays on dashboard so they can retry or see their data
  useEffect(() => {
    if (!tokenReady || loadingApplications) return;
    if (!applicationsFetchSucceeded || applications.length !== 0) return;

    const eligibilityData = localStorage.getItem("visaEligibilityAnswers");
    const responsesData = localStorage.getItem("visaEligibilityResponses");

    let hasPendingEligibility = false;
    try {
      if (eligibilityData && Object.keys(JSON.parse(eligibilityData)).length > 0) hasPendingEligibility = true;
      if (!hasPendingEligibility && responsesData && Object.keys(JSON.parse(responsesData)).length > 0) hasPendingEligibility = true;
    } catch {
      // ignore parse errors
    }

    if (hasPendingEligibility) {
      navigate("/answer-review", { replace: true });
    } else {
      // New user with no applications: show Choose Your Service page
      navigate("/services", { replace: true });
    }
  }, [tokenReady, loadingApplications, applicationsFetchSucceeded, applications.length, navigate]);

  const cardsData: VisaApplicationCardData[] = [
    {
      title: "Green Card for Spouse",
      label: "I-130",
      status: "Failed",
      statusColor: "#FBE6E6",
      statusBorderColor: "#B52222",
      statusTextColor: "#B52222",
      updatedDate: "May 4, 2025",
      totalSteps: 10,
      completedSteps: 0,
      missingDocs: 2,
      dotColor: "#B52222",
      buttons: [{ label: "Contact to Attorney", type: "secondary", link: "" }],
    },
    {
      title: "Green Card for Spouse",
      label: "H-1B",
      status: "Feedback Recieved",
      statusColor: "#FFF6E5",
      statusTextColor: "#DA9A1A",
      statusBorderColor: "#DA9A1A",
      updatedDate: "Apr 18, 2025",
      totalSteps: 8,
      completedSteps: 3,
      missingDocs: 1,
      dotColor: "#00B894",
      buttons: [
        { label: "View Feedback", type: "primary", link: "/visa-application" },
      ],
    },
    {
      title: "Green Card for Spouse",
      label: "F-1",
      status: "Approved",
      statusColor: "#E7FFD8",
      statusBorderColor: "#348400",
      statusTextColor: "#348400",
      updatedDate: "Mar 10, 2025",
      totalSteps: 6,
      completedSteps: 6,
      missingDocs: 0,
      dotColor: "#00B894",
      buttons: [
        { label: "View Details", type: "primary", link: "" },
        { label: "Download Form", type: "secondary", link: "" },
      ],
    },
    {
      title: "Green Card for Spouse",
      label: "F-1",
      status: "Pending",
      statusColor: "#E5E7EB",
      statusBorderColor: "#E5E7EB",
      statusTextColor: "#1B365D",
      updatedDate: "Mar 10, 2025",
      totalSteps: 6,
      completedSteps: 6,
      missingDocs: 0,
      dotColor: "#00B894",
      buttons: [{ label: "Proceed to Finish", type: "primary", link: "" }],
    },
  ];

  // Transform applications data to VisaReportCardData format
  const reportsData: VisaReportCardData[] = applications.map((app) => {
    const tierChip = ELIGIBILITY_TIER_CHIP[getEligibilityTier(app) ?? "green"];
    const visaTitles: { [key: string]: string } = {
      "N-400": "N-400 - Citizenship Application",
      "I-90": "I-90 - Green Card Renewal/Replacement",
      "I-751": "I-751 - Green Card Conditions Removal",
      "I-131": "I-131 - Travel Document Application",
      "I-130": "I-130 - Family Sponsorship",
    };

    const formatCheckedDate = (dateString: string) => {
      const formatted = formatDateUSReadable(dateString);
      return formatted || '';
    };

    const eligibilityCta = getEligibilityCardCTA(app);
    const buttons: Button[] = eligibilityCta.buttons;

    const reportBeneficiary =
      typeof app.beneficiaryName === "string" ? app.beneficiaryName.trim() : "";
    return {
      title: visaTitles[app.visaType] || app.visaType,
      label: tierChip.label,
      updatedDate: formatCheckedDate(app.updatedAt || app.createdAt),
      beneficiaryName: reportBeneficiary || undefined,
      labelBgColor: tierChip.bg,
      labelColor: tierChip.text,
      buttons,
      applicationId: app.id,
    };
  });
  const hasStartedApps = hasStartedApplications(applications);

  const [downloadingReportApplicationId, setDownloadingReportApplicationId] =
    useState<number | null>(null);

  const handleDownloadReport = async (card: VisaReportCardData) => {
    const applicationId = card.applicationId;
    if (applicationId == null) {
      return;
    }
    try {
      setDownloadingReportApplicationId(applicationId);
      // Server renders the report PDF (compact vector text) and streams it back;
      // no hidden DOM capture or client-side PDF generation.
      await downloadEligibilityReportPdf(applicationId);
    } catch (err) {
      console.error("Failed to download eligibility report", err);
    } finally {
      setDownloadingReportApplicationId(null);
    }
  };

  return (
    <>
      {/* Payment Success/Cancel Popup */}
      {paymentPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 md:p-8 relative">
            {loadingPayment ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B365D] mx-auto mb-4"></div>
                <p className="text-[#1B365D]">Confirming payment...</p>
              </div>
            ) : paymentPopup.type === "success" ? (
              <>
                <img
                  src={check}
                  alt="Success"
                  className="w-[80px] h-[80px] mx-auto mb-6 rounded-full"
                />
                <h2 className="text-[28px] text-[#1e2b5e] mb-4 font-georgia text-center">
                  Payment Successful!
                </h2>
                <p className="text-[#1B365D] mb-6 text-center">
                  Thank you for your payment. Your transaction has been
                  completed successfully.
                </p>

                {paymentPopup.paymentDetails && (
                  <div className="bg-[#f1f2f6] p-6 rounded-lg mb-6">
                    <h3 className="font-bold text-[#1B365D] mb-4 text-center">
                      Payment Details
                    </h3>
                    <div className="space-y-2 text-center">
                      <p className="text-[#1B365D]">
                        <span className="font-semibold">Amount:</span> $
                        {typeof paymentPopup.paymentDetails.amount === "number"
                          ? paymentPopup.paymentDetails.amount.toFixed(2)
                          : parseFloat(
                              paymentPopup.paymentDetails.amount || "0"
                            ).toFixed(2)}
                      </p>
                      <p className="text-[#1B365D]">
                        <span className="font-semibold">Status:</span>{" "}
                        <span className="capitalize">
                          {paymentPopup.paymentDetails.status}
                        </span>
                      </p>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setPaymentPopup(null)}
                  className="w-full bg-[#F4A261] text-[#1B365D] border-2 border-[#F4A261] font-semibold px-8 py-3 rounded-[50px] text-xl hover:bg-[#F29449]"
                >
                  Continue
                </button>
              </>
            ) : (
              <>
                <img
                  src={cross}
                  alt="Cancelled"
                  className="w-[80px] h-[80px] mx-auto mb-6"
                />
                <h2 className="text-[28px] text-[#1e2b5e] mb-4 font-georgia text-center">
                  Payment Cancelled
                </h2>
                <p className="text-[#1B365D] mb-6 text-center">
                  Your payment was cancelled. No charges have been made to your
                  account.
                </p>

                <button
                  onClick={() => setPaymentPopup(null)}
                  className="w-full bg-[#F4A261] text-[#1B365D] border-2 border-[#F4A261] font-semibold px-8 py-3 rounded-[50px] text-xl hover:bg-[#F29449]"
                >
                  Continue
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="page_main page_main-after-login mt-[100px] pt-[40px] bg-[#F7F8FA]">
        <div className="custom-container">
          {loadingApplications ? (
            <>
              <div className="space-y-3">
                <div className="h-9 w-64 animate-pulse rounded bg-slate-200" />
                <div className="h-6 w-96 animate-pulse rounded bg-slate-200" />
              </div>
              <div className="grid grid-cols-1 items-stretch gap-4 py-[30px] md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div
                    key={`eligibility-skeleton-${idx}`}
                    className="flex min-h-[250px] flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
                  >
                    <div className="flex-1 space-y-3">
                      <div className="h-5 w-3/4 animate-pulse rounded bg-slate-200" />
                      <div className="h-4 w-2/3 animate-pulse rounded bg-slate-200" />
                      <div className="h-5 w-24 animate-pulse rounded-full bg-slate-200" />
                      <div className="h-4 w-1/2 animate-pulse rounded bg-slate-200" />
                    </div>
                    <div className="mt-4 flex items-stretch gap-2 pt-4">
                      <div className="h-10 flex-1 animate-pulse rounded-full bg-slate-200" />
                      <div className="h-10 w-10 animate-pulse rounded-full bg-slate-200" />
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {!loadingApplications && hasStartedApps ? (
            <>
              <div className="md:flex items-center justify-between">
                <div>
                  <h1 className="text-[28px]">My Visa Applications</h1>
                  <p className="text-[18px]">
                    Track and manage your visa applications
                  </p>
                </div>
                <div className="md:mt-0 mt-4">
                  <StartNewApplicationDropdown />
                </div>
              </div>

              <div className="grid grid-cols-1 items-stretch md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 py-[50px]">
                {applicationCardsData.length > 0 ? applicationCardsData.map((card, index) => (
                  <div key={index} className="flex h-full min-h-0">
                    <VisaApplicationCard
                      title={card.title}
                      label={card.label}
                      status={card.status}
                      statusColor={card.statusColor}
                      statusTextColor={card.statusTextColor}
                      statusBorderColor={card.statusBorderColor}
                      updatedDate={card.updatedDate}
                      totalSteps={card.totalSteps}
                      completedSteps={card.completedSteps}
                      missingDocs={card.missingDocs}
                      rightArrow={rightArrow}
                      buttonStyleSm={buttonStyleSm}
                      buttonloginstyle2Sm={buttonloginstyle2Sm}
                      dotColor={card.dotColor}
                      buttons={card.buttons}
                      applicationId={card.applicationId}
                      visaType={card.visaType}
                      beneficiaryName={card.beneficiaryName}
                      hasUnreadFeedback={card.hasUnreadFeedback}
                      feedbackPendingItemCount={card.feedbackPendingItemCount}
                      hasDocumentFeedback={card.hasDocumentFeedback}
                      documentFeedbackPendingCount={card.documentFeedbackPendingCount}
                      documentFeedbackItems={card.documentFeedbackItems}
                    />
                  </div>
                )) : (
                  <div className="col-span-full text-center py-12">
                    <p className="text-[#1B365D] text-lg mb-4">
                      You haven't started any visa applications yet.
                    </p>
                    <Link
                      to="/start-new-application"
                      className="inline-block bg-[#F4A261] text-[#1B365D] px-6 py-3 rounded-full font-semibold hover:bg-[#F29449] transition-colors"
                    >
                      Start New Application
                    </Link>
                  </div>
                )}
              </div>
            </>
          ) : null}

          {!loadingApplications ? (
            <div className="md:flex items-center justify-between gap-4">
              <div>
                <h1 className="heading-lg">Check Eligibility Reports</h1>
                <p className="text-[18px] mt-2 ">
                  View your completed eligibility assessments
                </p>
              </div>
              {/*
               * Header CTA so applicants — including those whose existing
               * eligibility check returned "Not Eligible" — can always
               * start a fresh check for a different visa type. Hidden
               * when there are no reports yet: the empty-state hero
               * below already surfaces a Start Eligibility Check CTA.
               * Routes to /services so the user picks a visa first
               * instead of being dropped into a hard-coded N-400 flow.
               */}
              {reportsData.length > 0 && (
                <Link
                  to="/services"
                  style={buttonStyleSm}
                  className="mt-4 md:mt-0 inline-flex items-center justify-center whitespace-nowrap shrink-0"
                  aria-label="Check eligibility for a new visa"
                >
                  Check New Eligibility
                </Link>
              )}
            </div>
          ) : null}

          {!loadingApplications ? (
            <div className="grid grid-cols-1 items-stretch md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4 py-[50px]">
              {reportsData.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <p className="text-[#1B365D] text-lg mb-4">
                  You haven't checked your eligibility yet.
                </p>
                <Link
                  to="/visa-eligibility/N-400"
                  className="inline-block bg-[#F4A261] text-[#1B365D] px-6 py-3 rounded-full font-semibold hover:bg-[#F29449] transition-colors"
                >
                  Start Eligibility Check
                </Link>
              </div>
            ) : (
                reportsData.map((card, index) => (
                  <div key={index} className="flex h-full min-h-0">
                    <VisaReportCard
                      title={card.title}
                      label={card.label}
                      updatedDate={card.updatedDate}
                      beneficiaryName={card.beneficiaryName}
                      buttonStyleSm={buttonStyleSm}
                      buttonStyleSecondarySm={buttonStyleSecondarySm}
                      buttonStyleFiledSm={buttonStyleFiledSm}
                      labelColor={card.labelColor}
                      labelBgColor={card.labelBgColor}
                      buttons={card.buttons}
                      onDownload={() => handleDownloadReport(card)}
                      isDownloadingReport={
                        card.applicationId != null &&
                        card.applicationId === downloadingReportApplicationId
                      }
                    />
                  </div>
                ))
              )}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default Dashboard;
